import styled from 'styled-components'

import {useState} from 'react'
import {useRouter} from 'next/router'
import classNames from 'classnames'
import {palette} from 'styled-tools'
import {media} from 'styled-bootstrap-grid'
import {
  useHits,
  useRefinementList,
  useClearRefinements,
  useCurrentRefinements,
} from 'react-instantsearch'
import EqualizerLineIcon from 'remixicon-react/EqualizerLineIcon'
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon'
import ArrowRightSLineIcon from 'remixicon-react/ArrowRightSLineIcon'

import {H4, H5} from '@festi/common/components/typography'
import {VariantList} from '@festi/common/api/rest'
import {fluidRange} from '@festi/common/utils/styles'
import {Expandable} from '@festi/common/components/common'
import {CloseButton, MainButton} from '@festi/common/components/buttons'

import SideDrawer, {DrawerFooter, DrawerHeader} from './SideDrawer'
import {CountCircle} from './CustomRefinement'
import {facetNames} from '../utilites/facets'
import {refinementStyle} from '../utilites/refinementStyle'
import RangeRefinement from './refinment/RangeRefinment'
import {priceRangeAttribute} from '../utilites/refinement'
import HierarchicalRefinement from './refinment/HierarchicalRefinement'
import ChoiceRefinement from './refinment/ChoiceRefinement'

const Root = styled.div`
  position: relative;
  height: 100%;
  min-height: calc(100vh - 150px);
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 80px;
  ${fluidRange('padding-bottom', '12px', '48px', 'xs', 'md')};
  ${fluidRange('padding-left', '16px', '56px', 'xs', 'md')};
  ${fluidRange('padding-right', '16px', '56px', 'xs', 'md')};
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .remixicon-icon {
    fill: ${palette('ui20Solid')};
  }
`

const Close = styled.div`
  .remixicon-icon {
    fill: ${palette('blue')};
  }

  ${media.md`
    display: none;
  `}
`

const NavWrapper = styled.div`
  padding-bottom: 32px;
`

const RefineWrapper = styled.div`
  border-bottom: 1px solid ${palette('border')};

  &.hide {
    display: none;
  }
`

const RefineButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 0;
  border: none;
  background-color: transparent;
  cursor: pointer;

  .remixicon-icon {
    fill: ${palette('ui20Solid')};
  }
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const Clear = styled.div`
  color: ${palette('blue')};
  text-decoration: underline;
  cursor: pointer;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 20px;

  ${refinementStyle}
`

const Block = styled.div`
  width: 100%;
  height: 120px;
`

interface StickyProps {
  nbHits: number
  onRequestClose: () => void
}

function StickyButtons({nbHits, onRequestClose}: StickyProps) {
  const {items} = useCurrentRefinements()
  const {refine: clearRefine} = useClearRefinements()

  const hasRefinements = items?.length

  if (!hasRefinements) return null

  return (
    <>
      <Block />

      <DrawerFooter>
        <MainButton
          label="Hreinsa síur"
          buttonVariant="ghost"
          onClick={clearRefine}
          fullWidth
        />

        <MainButton
          label={`Skoða ${nbHits || 0} vörur`}
          onClick={onRequestClose}
          fullWidth
        />
      </DrawerFooter>
    </>
  )
}

function PriceRangeAttributeItem() {
  const [expand, setExpand] = useState(false)
  const {items} = useRefinementList({
    attribute: priceRangeAttribute,
    limit: 200,
  })
  const {refine: clearRefine} = useClearRefinements({
    includedAttributes: [priceRangeAttribute],
  })

  const [hasRefinement, setHasRefinement] = useState(0)

  return (
    <RefineWrapper>
      <RefineButton onClick={() => setExpand(!expand)}>
        <H5>Verð</H5>
        <Flex>
          {!!hasRefinement && (
            <Flex>
              <Clear onClick={() => clearRefine()}>Hreinsa</Clear>
              <CountCircle>{hasRefinement}</CountCircle>
            </Flex>
          )}
          {!expand ? <ArrowRightSLineIcon /> : <ArrowDownSLineIcon />}
        </Flex>
      </RefineButton>

      <Expandable expanded={expand} key={`refinement-${priceRangeAttribute}`}>
        <List>
          <RangeRefinement
            items={items}
            onChange={(value: number) => setHasRefinement(value)}
          />
        </List>
      </Expandable>
    </RefineWrapper>
  )
}

function CategoryAttributeItem() {
  const attribute = 'categories'
  const [expand, setExpand] = useState(false)

  const {items, refine} = useRefinementList({
    attribute,
    limit: 200,
  })
  const {refine: clearRefine} = useClearRefinements({
    includedAttributes: [attribute],
  })

  const numberRefined = items?.filter((i) => i.isRefined)?.length

  return (
    <RefineWrapper>
      <RefineButton onClick={() => setExpand(!expand)}>
        <H5>Flokkar</H5>
        <Flex>
          {!!numberRefined && (
            <Flex>
              <Clear onClick={() => clearRefine()}>Hreinsa</Clear>
              <CountCircle>{numberRefined}</CountCircle>
            </Flex>
          )}
          {!expand ? <ArrowRightSLineIcon /> : <ArrowDownSLineIcon />}
        </Flex>
      </RefineButton>

      <Expandable expanded={expand} key={`refinement-${priceRangeAttribute}`}>
        <List>
          <HierarchicalRefinement
            items={items}
            attribute={attribute}
            refine={refine}
          />
        </List>
      </Expandable>
    </RefineWrapper>
  )
}

interface ItemProps {
  label: string
  attribute: string
  attrNameMap?: Record<string, string>
  displayNone?: boolean
}

export function RefinementItem({label, attribute, displayNone}: ItemProps) {
  const [expand, setExpand] = useState(false)
  const {items, refine} = useRefinementList({
    attribute: attribute,
    limit: 200,
  })
  const {refine: clearRefine} = useClearRefinements({
    includedAttributes: [attribute],
  })

  const numberRefined = items?.filter((i) => i.isRefined)?.length

  const hide = displayNone || items?.length === 0 || attribute === 'categories'

  return (
    <RefineWrapper className={classNames({hide})}>
      <RefineButton onClick={() => setExpand(!expand)}>
        <H5>{label}</H5>
        <Flex>
          {!!numberRefined && (
            <Flex>
              <Clear onClick={() => clearRefine()}>Hreinsa</Clear>
              <CountCircle>{numberRefined}</CountCircle>
            </Flex>
          )}
          {!expand ? <ArrowRightSLineIcon /> : <ArrowDownSLineIcon />}
        </Flex>
      </RefineButton>

      <Expandable expanded={expand} key={`refinement-${attribute}`}>
        <List>
          <ChoiceRefinement items={items} refine={refine} />
        </List>
      </Expandable>
    </RefineWrapper>
  )
}

interface Props {
  onWeb?: boolean
  isOpen?: boolean
  attrNameMap: Record<string, string>
  onRequestClose?: () => void
}

export default function RefinementDrawer({
  isOpen,
  attrNameMap,
  onRequestClose,
}: Props) {
  const {query} = useRouter()
  const hits = useHits<VariantList>(query)

  const facets = hits?.results?.renderingContent?.facetOrdering?.facets?.order

  return (
    <SideDrawer isOpen={isOpen} onRequestClose={onRequestClose}>
      <Root>
        <DrawerHeader>
          <Title>
            <div>
              <EqualizerLineIcon size={24} />
            </div>
            <H4>Síur</H4>

            <Close>
              <CloseButton onClick={onRequestClose} />
            </Close>
          </Title>
        </DrawerHeader>

        <ContentWrapper>
          <NavWrapper>
            <PriceRangeAttributeItem />

            <CategoryAttributeItem />

            {facets?.map((attribute: string) => (
              <RefinementItem
                key={attribute}
                label={facetNames(attrNameMap)?.[attribute] || attribute}
                attribute={attribute}
              />
            ))}
          </NavWrapper>
        </ContentWrapper>

        <StickyButtons
          nbHits={hits.results.nbHits}
          onRequestClose={onRequestClose}
        />
      </Root>
    </SideDrawer>
  )
}
